import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Paper,
  IconButton,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import CloseIcon from "@material-ui/icons/Close";
import SummarizeIcon from "@material-ui/icons/Description";
import { generateConversationSummary } from "../../services/apiGpt";
import { i18n } from "../../translate/i18n";
import { getGPTTranslation, mapI18nToGPTLanguage } from "../../utils/gptTranslations";

const useStyles = makeStyles((theme) => ({
  dialog: {
    [theme.breakpoints.up('sm')]: {
      minWidth: 600,
    },
  },
  dialogContent: {
    padding: theme.spacing(2),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(1),
    },
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  headerTitle: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  controlsContainer: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  typeSelector: {
    width: "100%",
    maxWidth: 300,
  },
  generateButton: {
    alignSelf: "flex-start",
    padding: theme.spacing(1.5, 4),
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: 1,
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: theme.spacing(4),
  },
  summaryPaper: {
    padding: theme.spacing(3),
    marginTop: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
  },
  summaryText: {
    whiteSpace: "pre-wrap",
    lineHeight: 1.7,
  },
  sectionTitle: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
    fontWeight: 600,
  },
  chipContainer: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  errorContainer: {
    padding: theme.spacing(2),
    backgroundColor: "#ffebee",
    color: "#c62828",
    borderRadius: theme.shape.borderRadius,
    border: "1px solid #ef9a9a",
  },
  placeholder: {
    textAlign: "center",
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
}));

const ConversationSummaryModal = ({ open, onClose, ticketId }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [summaryType, setSummaryType] = useState("short");

  // Get current language for translations
  const currentLang = mapI18nToGPTLanguage(i18n.language);
  const t = (key, params) => getGPTTranslation(key, currentLang, params);

  useEffect(() => {
    if (!open) {
      setSummary(null);
      setError(null);
    }
  }, [open]);

  const loadSummary = async () => {
    if (!ticketId) {
      setError(i18n.t("gptModals.noTicketSelected") || "No ticket selected");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const systemLang = i18n.language || localStorage.getItem("language") || "en";
      const data = await generateConversationSummary(ticketId, summaryType, systemLang);
      setSummary(data);
    } catch (err) {
      console.error("[ConversationSummaryModal] Error:", err);
      setError(err.response?.data?.error || err.message || "Failed to generate summary");
    } finally {
      setLoading(false);
    }
  };

  const getSentimentColor = (sentiment) => {
    switch (sentiment) {
      case "positive":
        return "primary";
      case "negative":
        return "secondary";
      default:
        return "default";
    }
  };

  const translateSentiment = (sentiment) => {
    if (!sentiment) return "N/A";
    const key = sentiment.toLowerCase();
    if (key === "positive") return t("positive");
    if (key === "negative") return t("negative");
    if (key === "neutral") return t("neutral");
    return sentiment;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      classes={{ paper: classes.dialog }}
    >
      <DialogTitle>
        <Box className={classes.header}>
          <Box className={classes.headerTitle}>
            <SummarizeIcon color="primary" />
            <Typography variant={isMobile ? "subtitle1" : "h6"}>📝 {t("conversationSummary")}</Typography>
          </Box>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent className={classes.dialogContent}>
        <Box className={classes.controlsContainer}>
          <FormControl variant="outlined" className={classes.typeSelector}>
            <InputLabel>{t("summaryLength")}</InputLabel>
            <Select
              value={summaryType}
              onChange={(e) => setSummaryType(e.target.value)}
              label={t("summaryLength")}
            >
              <MenuItem value="short">{t("summaryShort")} (200-300)</MenuItem>
              <MenuItem value="extended">{t("summaryLong")} (500-800)</MenuItem>
            </Select>
          </FormControl>
          
          <Button
            variant="contained"
            color="primary"
            onClick={loadSummary}
            disabled={loading}
            className={classes.generateButton}
          >
            {loading ? t("generating") : t("generateSuggestions").replace("Suggestions", t("conversationSummary").split(" ")[0])}
          </Button>
        </Box>

        {loading ? (
          <Box className={classes.loadingContainer}>
            <CircularProgress />
            <Typography variant="body2" style={{ marginTop: 16 }}>
              {t("generatingSummary", { type: summaryType === "short" ? t("summaryShort") : t("summaryLong") })}
            </Typography>
          </Box>
        ) : error ? (
          <Box className={classes.errorContainer}>
            <Typography variant="body2">
              <strong>Error:</strong> {error}
            </Typography>
          </Box>
        ) : summary ? (
          <Paper className={classes.summaryPaper} elevation={0}>
            {/* Summary Text */}
            <Typography variant="body1" className={classes.summaryText}>
              {summary.summary}
            </Typography>

            {/* Key Points */}
            {summary.keyPoints && summary.keyPoints.length > 0 && (
              <>
                <Typography variant="subtitle1" className={classes.sectionTitle}>
                  🎯 {t("keyPoints")}
                </Typography>
                <ul>
                  {summary.keyPoints.map((point, index) => (
                    <li key={index}>
                      <Typography variant="body2">{point}</Typography>
                    </li>
                  ))}
                </ul>
              </>
            )}

            {/* Main Topics */}
            {summary.mainTopics && summary.mainTopics.length > 0 && (
              <>
                <Typography variant="subtitle1" className={classes.sectionTitle}>
                  📌 {t("mainTopics")}
                </Typography>
                <Box className={classes.chipContainer}>
                  {summary.mainTopics.map((topic, index) => (
                    <Chip key={index} label={topic} variant="outlined" />
                  ))}
                </Box>
              </>
            )}

            {/* Sentiment */}
            {summary.sentiment && (
              <>
                <Typography variant="subtitle1" className={classes.sectionTitle}>
                  😊 {t("sentimentPerformance")}
                </Typography>
                <Chip
                  label={translateSentiment(summary.sentiment)}
                  color={getSentimentColor(summary.sentiment)}
                />
              </>
            )}

            {/* Recommendations */}
            {summary.recommendations && summary.recommendations.length > 0 && (
              <>
                <Typography variant="subtitle1" className={classes.sectionTitle}>
                  💡 {t("recommendations")}
                </Typography>
                <ul>
                  {summary.recommendations.map((rec, index) => (
                    <li key={index}>
                      <Typography variant="body2">{rec}</Typography>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </Paper>
        ) : (
          <Typography variant="body2" className={classes.placeholder}>
            {t("noSummaryAvailable")}
          </Typography>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} color="primary">
          {t("close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConversationSummaryModal;
